/* eslint-disable @typescript-eslint/no-unused-vars */
import { of, switchMap, catchError, filter, withLatestFrom, map, tap } from 'rxjs';
import { Store } from '@ngrx/store';
import { AppState } from 'src/app/state/app.state';
import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { WorkspaceTemplateService } from '../services/templates.service';
import { TemplateError, templateFeature, TemplateFetch, TemplateFetched } from './template.feature';
import {
  WorkspaceError,
  WorkspaceFetch,
  WorkspaceFetched,
  WorkspaceListError,
  workspaceListFeature,
  WorkspaceListFetch,
  WorkspaceListFetched
} from './workspace-list.feature';
import { WorkspaceService } from '../services/workspace.service';
import { CollectionFetch, CollectionFetched, CollectionError, collectionFeature } from './collection.feature';
import { CollectionsService } from '../../services/collections.service';
import {
  CollectionDocError,
  collectionDocFeature,
  CollectionDocFetch,
  CollectionDocFetched
} from './collection-doc.feature';
import { WorkspaceDocsCollectionsService } from '../services/document.service';
import {
  WorkspaceSourceDocFetch,
  workspaceSourceDocFeature,
  WorkspaceSourceDocFetched,
  WorkspaceSourceDocError
} from './workspace-source-doc.feature';
import {
  WorkspaceFileDelete,
  WorkspaceFileDeleted,
  WorkspaceFileError,
  workspaceFileFeature
} from './workspace-file-delete.feature';
import {
  WorkspaceCollectionDelete,
  workspaceCollectionFeature,
  WorkspaceCollectionDeleted,
  WorkspaceCollectionError
} from './workspace-collection-delete.feature';
import {
  UserManagementError,
  userManagementFeature,
  UserManagementFetch,
  UserManagementFetched
} from './user-management.feature';
import { WorkspaceUserService } from '../services/user.service';
import { UserDelete, UserDeleted, UserDeleteError, userDeleteFeature } from './user-delete.feature';
import {
  UserRoleChange,
  UserRoleChanged,
  UserRoleChangeError,
  userRoleChangeFeature
} from './user-role-change.feature';
import { personaFeature, PersonaFetch, PersonaFetched } from './persona.feature';
import { PersonaService } from '../../services/persona.service';
import {
  WorkspacePersonaError,
  workspacePersonaFeature,
  WorkspacePersonaFetch,
  WorkspacePersonaFetched
} from './workspace-persona.feature';
import { WorkspacePersonaService } from '../services/persona.service';

import { taskListFeature, TaskListFetch, TaskListFetched } from './task-list.feature';
import { WorkspaceTaskService } from '../services/task.service';
import { TaskDetailsError, taskDetailsFeature, TaskDetailsFetch, TaskDetailsFetched } from './task-details.feature';
import { TaskHistoryError, TaskHistoryFetch, TaskHistoryFetched } from './task-history.feature';
import { TaskCommentError, taskCommentFeature, TaskCommentFetch, TaskCommentFetched } from './task-comments.feature';
import {
  WorkspaceAgentError,
  workspaceAgentFeature,
  WorkspaceAgentFetch,
  WorkspaceAgentFetched
} from './agent-status.feature';
import { WorkspaceAgentService } from '@workspaces/services/agent.service';
import { HttpErrorResponse } from '@angular/common/http';
import { WorkspaceVersionFetch, workspaceVersionFeature, WorkspaceVersionFetched } from './workspace-version.feature';
import { WorkspaceVersionService } from '../services/version.service';
import { DatePipe } from '@angular/common';

@Injectable()
export class WorkspaceEffects {
  constructor(
    private readonly actions$: Actions,
    private readonly store: Store<AppState>,
    private readonly workspaceTemplateService: WorkspaceTemplateService,
    private readonly workspaceService: WorkspaceService,
    private readonly collectionsService: CollectionsService,
    private readonly workspaceDocsCollectionsService: WorkspaceDocsCollectionsService,
    private readonly workspaceUserService: WorkspaceUserService,
    private readonly personaService: PersonaService,
    private readonly workspacePersonaService: WorkspacePersonaService,
    private readonly workspaceTaskService: WorkspaceTaskService,
    private readonly workspaceAgentService: WorkspaceAgentService,
    private readonly workspaceVersionService: WorkspaceVersionService,
    private readonly datePipe: DatePipe
  ) {}

  templateFetch$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TemplateFetch),
      withLatestFrom(this.store.select(templateFeature.templateHasPendingRequest)),
      filter(([_, hasPendingRequest]) => !hasPendingRequest),
      switchMap(([action]) =>
        this.workspaceTemplateService.getTemplates(action).pipe(
          switchMap(templates => of(TemplateFetched({ templates }))),
          catchError(() => of(TemplateError()))
        )
      )
    )
  );

  workspaceListFetch$ = createEffect(() =>
    this.actions$.pipe(
      ofType(WorkspaceListFetch),
      withLatestFrom(this.store.select(workspaceListFeature.workspaceListHasPendingRequest)),
      filter(([_, hasPendingRequest]) => !hasPendingRequest),
      switchMap(([action]) =>
        this.workspaceService.getWorkspaces(action).pipe(
          switchMap(workspaceList => of(WorkspaceListFetched({ workspaceList }))),
          catchError(() => of(WorkspaceListError()))
        )
      )
    )
  );

  collectionFetch$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CollectionFetch),
      withLatestFrom(this.store.select(collectionFeature.collectionHasPendingRequest)),
      filter(([_, hasPendingRequest]) => !hasPendingRequest),
      switchMap(([action]) =>
        this.collectionsService.getCollections(action.includeHidden).pipe(
          switchMap(collection => of(CollectionFetched({ collection }))),
          catchError(() => of(CollectionError()))
        )
      )
    )
  );
  collectionDocFetch$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CollectionDocFetch),
      switchMap(action =>
        this.store.select(collectionDocFeature.collectionDocHasPendingRequest(action.collectionId)).pipe(
          map((hasPendingRequest: boolean) => ({
            action,
            hasPendingRequest
          }))
        )
      ),

      filter(({ hasPendingRequest }) => !hasPendingRequest),
      switchMap(({ action }) =>
        this.collectionsService.getCollectionData(action.collectionId).pipe(
          switchMap(collectionDoc =>
            of(
              CollectionDocFetched({
                collection: collectionDoc,
                collectionId: action.collectionId
              })
            )
          ),
          catchError(() => of(CollectionDocError({ collectionId: action.collectionId })))
        )
      )
    )
  );
  workspaceSourceDocFetch$ = createEffect(() =>
    this.actions$.pipe(
      ofType(WorkspaceSourceDocFetch),
      switchMap(action =>
        this.store.select(workspaceSourceDocFeature.workspaceSourceDocHasPendingRequest(action.workspaceId)).pipe(
          map(hasPendingRequest => ({
            action,
            hasPendingRequest
          }))
        )
      ),
      filter(({ hasPendingRequest }) => !hasPendingRequest),
      switchMap(({ action }) =>
        this.workspaceDocsCollectionsService.getWorkspaceDocuments(action.workspaceId).pipe(
          switchMap(workspaceSourceDoc =>
            of(
              WorkspaceSourceDocFetched({
                workspaceId: action.workspaceId,
                workspaceSourceDoc: workspaceSourceDoc
              })
            )
          ),
          catchError(() => of(WorkspaceSourceDocError({ workspaceId: action.workspaceId })))
        )
      )
    )
  );
  workspaceFileDelete$ = createEffect(() =>
    this.actions$.pipe(
      ofType(WorkspaceFileDelete),
      switchMap(action =>
        this.store.select(workspaceFileFeature.workspaceFileHasPendingRequest(action.fileId)).pipe(
          map(hasPendingRequest => ({
            action,
            hasPendingRequest
          }))
        )
      ),
      filter(({ hasPendingRequest }) => !hasPendingRequest),
      switchMap(({ action }) =>
        this.workspaceDocsCollectionsService.deleteWorkspaceFiles(action.workspaceId, { files: [action.fileId] }).pipe(
          switchMap(() =>
            of(
              WorkspaceFileDeleted({
                fileId: action.fileId
              }),
              WorkspaceSourceDocFetch({
                workspaceId: action.workspaceId
              })
            )
          ),
          catchError(() => of(WorkspaceFileError({ fileId: action.fileId })))
        )
      )
    )
  );
  workspaceCollectionDelete$ = createEffect(() =>
    this.actions$.pipe(
      ofType(WorkspaceCollectionDelete),
      switchMap(action =>
        this.store.select(workspaceCollectionFeature.workspaceCollectionHasPendingRequest(action.collectionId)).pipe(
          map(hasPendingRequest => ({
            action,
            hasPendingRequest
          }))
        )
      ),
      filter(({ hasPendingRequest }) => !hasPendingRequest),
      switchMap(({ action }) =>
        this.workspaceDocsCollectionsService
          .unlinkCollections(action.workspaceId, { collection_ids: [action.collectionId] })
          .pipe(
            switchMap(() =>
              of(
                WorkspaceCollectionDeleted({
                  collectionId: action.collectionId
                }),
                WorkspaceSourceDocFetch({
                  workspaceId: action.workspaceId
                })
              )
            ),
            catchError(() => of(WorkspaceCollectionError({ collectionId: action.collectionId })))
          )
      )
    )
  );

  userManagement$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserManagementFetch),
      withLatestFrom(this.store.select(userManagementFeature.userManagementHasPendingRequest)),
      filter(([_, hasPendingRequest]) => !hasPendingRequest),
      switchMap(([action]) =>
        this.workspaceUserService.getUsers(action.workspaceId).pipe(
          switchMap(users => of(UserManagementFetched({ users }))),
          catchError(() => of(UserManagementError()))
        )
      )
    )
  );
  userDelete$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserDelete),
      switchMap(action =>
        this.store.select(userDeleteFeature.userDeleteHasPendingRequest(action.userId)).pipe(
          map(hasPendingRequest => ({
            action,
            hasPendingRequest
          }))
        )
      ),
      filter(({ hasPendingRequest }) => !hasPendingRequest),
      switchMap(({ action }) =>
        this.workspaceUserService.revokeAccess(action.workspaceId, action.userId).pipe(
          switchMap(() =>
            of(
              UserDeleted({
                userId: action.userId
              }),
              UserManagementFetch({
                workspaceId: action.workspaceId
              })
            )
          ),
          catchError(() => of(UserDeleteError({ userId: action.userId })))
        )
      )
    )
  );

  userRoleChange$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserRoleChange),
      switchMap(action =>
        this.store.select(userRoleChangeFeature.userRoleChangeHasPendingRequest(action.userId)).pipe(
          map(hasPendingRequest => ({
            action,
            hasPendingRequest
          }))
        )
      ),
      filter(({ hasPendingRequest }) => !hasPendingRequest),
      switchMap(({ action }) =>
        this.workspaceUserService.changeRole(action.workspaceId, action.userId, { role: action.role }).pipe(
          switchMap(() =>
            of(
              UserRoleChanged({
                userId: action.userId
              }),
              UserManagementFetch({
                workspaceId: action.workspaceId
              })
            )
          ),
          catchError(() => of(UserRoleChangeError({ userId: action.userId })))
        )
      )
    )
  );

  persona$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PersonaFetch),
      withLatestFrom(this.store.select(personaFeature.personaHasPendingRequest)),
      filter(([_, hasPendingRequest]) => !hasPendingRequest),
      switchMap(([action]) =>
        this.personaService.getPersonas(action.includeHidden).pipe(
          switchMap(personas => of(PersonaFetched({ personas }))),
          catchError(() => of(UserManagementError()))
        )
      )
    )
  );

  workspacePersona$ = createEffect(() =>
    this.actions$.pipe(
      ofType(WorkspacePersonaFetch),
      switchMap(action =>
        this.store.select(workspacePersonaFeature.workspacePersonaHasPendingRequest(action.workspaceId)).pipe(
          map(hasPendingRequest => ({
            action,
            hasPendingRequest
          }))
        )
      ),
      filter(({ hasPendingRequest }) => !hasPendingRequest),
      switchMap(({ action }) =>
        this.workspacePersonaService.getWorkspacePersonas(action.workspaceId).pipe(
          switchMap(workspacePersonas =>
            of(
              WorkspacePersonaFetched({
                workspaceId: action.workspaceId,
                workspacePersonas
              })
            )
          ),
          catchError(() => of(WorkspacePersonaError({ workspaceId: action.workspaceId })))
        )
      )
    )
  );

  taskDetails$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TaskDetailsFetch),
      switchMap(action =>
        this.store.select(taskDetailsFeature.taskDetailsHasPendingRequest(action.taskId)).pipe(
          map(hasPendingRequest => ({
            action,
            hasPendingRequest
          }))
        )
      ),
      filter(({ hasPendingRequest }) => !hasPendingRequest),
      switchMap(({ action }) =>
        this.workspaceTaskService.getTaskById(action.workspaceId, action.taskId).pipe(
          switchMap(taskDetails =>
            of(
              TaskDetailsFetched({
                task: taskDetails
              })
            )
          ),
          catchError(() => of(TaskDetailsError({ taskId: action.taskId })))
        )
      )
    )
  );

  taskListFetch$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TaskListFetch),
      withLatestFrom(this.store.select(taskListFeature.taskListHasPendingRequest)),
      filter(([_, hasPendingRequest]) => !hasPendingRequest),
      switchMap(([action]) =>
        this.workspaceTaskService.getTasks(action.workspaceId).pipe(
          switchMap(taskList => of(TaskListFetched({ taskList }))),
          catchError(() => of(WorkspaceListError()))
        )
      )
    )
  );

  taskHistory$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TaskHistoryFetch),
      switchMap(action =>
        this.store.select(taskDetailsFeature.taskDetailsHasPendingRequest(action.taskId)).pipe(
          map(hasPendingRequest => ({
            action,
            hasPendingRequest
          }))
        )
      ),
      filter(({ hasPendingRequest }) => !hasPendingRequest),
      switchMap(({ action }) =>
        this.workspaceTaskService.getTaskHistory(action.workspaceId, action.taskId).pipe(
          switchMap(taskHistory =>
            of(
              TaskHistoryFetched({
                taskHistory: taskHistory,
                taskId: action.taskId
              })
            )
          ),
          catchError(() => of(TaskHistoryError({ taskId: action.taskId })))
        )
      )
    )
  );

  taskComment$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TaskCommentFetch),
      switchMap(action =>
        this.store.select(taskCommentFeature.taskCommentHasPendingRequest(action.taskId)).pipe(
          map(hasPendingRequest => ({
            action,
            hasPendingRequest
          }))
        )
      ),
      filter(({ hasPendingRequest }) => !hasPendingRequest),
      switchMap(({ action }) =>
        this.workspaceTaskService.getComments(action.workspaceId, action.taskId).pipe(
          switchMap(taskComment =>
            of(
              TaskCommentFetched({
                taskComment: taskComment,
                taskId: action.taskId
              })
            )
          ),
          catchError(() => of(TaskCommentError({ taskId: action.taskId })))
        )
      )
    )
  );
  worksapceStatus$ = createEffect(() =>
    this.actions$.pipe(
      ofType(WorkspaceAgentFetch),
      switchMap(action =>
        this.store.select(workspaceAgentFeature.workspaceAgentHasPendingRequest(action.workspaceId)).pipe(
          map(hasPendingRequest => ({
            action,
            hasPendingRequest
          }))
        )
      ),
      filter(({ hasPendingRequest }) => !hasPendingRequest),
      switchMap(({ action }) =>
        this.workspaceAgentService.getAgentRunStatus(action.workspaceId).pipe(
          switchMap(status =>
            of(
              WorkspaceAgentFetched({
                workspaceId: action.workspaceId,
                workspaceAgent: status
              })
            )
          ),
          catchError(err => {
            const httpErr = err as HttpErrorResponse;
            if (httpErr.status === 404 || httpErr.status === 0) {
              return of(
                WorkspaceAgentFetched({
                  workspaceId: action.workspaceId,
                  workspaceAgent: { status: 'STATUS_NOT_FOUND', workspace_id: action.workspaceId }
                })
              );
            }
            return of(WorkspaceAgentError({ workspaceId: action.workspaceId }));
          })
        )
      )
    )
  );
  workspaceFetch$ = createEffect(() =>
    this.actions$.pipe(
      ofType(WorkspaceFetch),
      switchMap(action =>
        this.workspaceService.getWorkspaceDetails(action.workspaceId).pipe(
          switchMap(workspace => of(WorkspaceFetched({ workspace, workspaceId: action.workspaceId }))),
          catchError(() => of(WorkspaceError()))
        )
      )
    )
  );

  workspaceVersionFetch$ = createEffect(() =>
    this.actions$.pipe(
      ofType(WorkspaceVersionFetch),
      switchMap(action =>
        this.store.select(workspaceVersionFeature.workspaceVersionHasPendingRequest(action.workspaceId)).pipe(
          map(hasPendingRequest => ({
            action,
            hasPendingRequest
          }))
        )
      ),
      filter(({ hasPendingRequest }) => !hasPendingRequest),
      switchMap(({ action }) =>
        this.workspaceVersionService.getVersion(action.workspaceId).pipe(
          switchMap(workspaceVersion =>
            of(
              WorkspaceVersionFetched({
                workspaceId: action.workspaceId,
                workspaceVersion: workspaceVersion.map(version => {
                  if (!version.restored_version_alias && version.restored_version_id) {
                    const restoreVersion = workspaceVersion.find(
                      restoreVersion => version.restored_version_id === restoreVersion.version_id
                    );
                    if (!restoreVersion) {
                      version.restored_version_alias = 'Unknown';
                    } else {
                      version.restored_version_alias = this.datePipe.transform(restoreVersion.created_at, 'short');
                    }
                  }
                  return version;
                })
              })
            )
          ),
          catchError(() => of(WorkspaceSourceDocError({ workspaceId: action.workspaceId })))
        )
      )
    )
  );
}
